import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useHistory } from "react-router-dom";
import useMounted from "../../hooks/useMounted";
import useSlideIn from "../../hooks/useSlideIn";
import AbsoluteContainer from "../containers/AbsoluteContainer";
import { Share, Lock, LockOpen, Trash2, Upload, Info, X } from "lucide-react";

import RelativeContainer from "../containers/RelativeContainer";
import TextInput from "../inputs/TextInput";
import SaveIcon from "../inputs/SaveIcon";

const encodeInputPath = (path) => {
  if (path && path !== "/ ") {
    if (path.indexOf("/ ") !== 0) {
      path = "/ " + path;
    }
    return path;
  } else {
    return null;
  }
};

function UserSection({ user, logout, slideInStyle = {} }) {
  const [showMenu, setShowMenu] = useState(false);
  const avatarRef = useRef(null);
  const history = useHistory();

  // Compute initials from user displayName or email
  // let initials = "";
  // if (user) {
  //   if (user.displayName) {
  //     const names = user.displayName.split(" ");
  //     initials = names
  //       .map((n) => n[0])
  //       .join("")
  //       .toUpperCase();
  //   } else if (user.email) {
  //     initials = user.email[0].toUpperCase();
  //   }
  // }

  // Close menu on outside click
  useEffect(() => {
    if (!showMenu) return;
    function handleClick(e) {
      if (avatarRef.current && !avatarRef.current.contains(e.target)) {
        setShowMenu(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [showMenu]);

  if (!user) {
    return (
      <div
        onClick={() => history.push("/login")}
        className="login-button"
        style={{
          fontSize: "30px",
          cursor: "pointer",
          backdropFilter: "blur(10px)",
          ...slideInStyle,
        }}
      >
        login
      </div>
    );
  }

  return (
    <div
      style={{ position: "relative", zIndex: 1003, ...slideInStyle }}
      ref={avatarRef}
      id="absolute-mobile"
    >
      {/* User name button */}
      <div
        className="user-name-button"
        style={{
          fontWeight: 400,
          marginBottom: 4,
          cursor: "pointer",
          fontSize: "32px",
        }}
        onClick={() => setShowMenu((v) => !v)}
      >
        {user.displayName?.split(" ")[0].toLowerCase() ||
          user.email?.split("@")[0] ||
          "User"}
      </div>
      {/* Dropdown menu */}
      {showMenu && (
        <div
          style={{
            position: "absolute",
            top: 48,
            right: 0,
            minWidth: 200,
            background: "#fff",
            color: "#222",
            borderRadius: 8,
            boxShadow: "0 4px 16px rgba(0,0,0,0.18)",
            padding: "16px",
            zIndex: 1001,
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            fontSize: 15,
            fontWeight: 400,
          }}
        >
          <div style={{ fontWeight: 700, marginBottom: 4 }}>
            {user.displayName || user.email?.split("@")[0] || "User"}
          </div>

          {user.email && (
            <div style={{ fontSize: 13, color: "#777", marginBottom: 12 }}>
              {user.email}
            </div>
          )}
          <button
            style={{
              background: "#4f46e5",
              color: "#fff",
              border: "none",
              borderRadius: 6,
              padding: "8px 16px",
              fontWeight: 600,
              fontSize: 14,
              cursor: "pointer",
              alignSelf: "stretch",
              transition: "background 0.18s",
            }}
            onClick={logout}
          >
            Log out
          </button>
        </div>
      )}
    </div>
  );
}

function Header({ user, onCreateClick, logout, onTitleClick }) {
  const [clickCount, setClickCount] = useState(0);
  const clickTimeoutRef = useRef(null);
  const [showInfoModal, setShowInfoModal] = useState(false);

  const titleSlideIn = useSlideIn({
    show: true,
    y: -30,
    delay: 100,
    duration: 300,
    opacity: 1,
  });

  const createButtonSlideIn = useSlideIn({
    show: true,
    y: -30,
    delay: 600,
    duration: 300,
    opacity: 1,
  });

  const userSlideIn = useSlideIn({
    show: true,
    y: -30,
    delay: 1000,
    duration: 300,
    opacity: 1,
  });

  const handleTitleClick = () => {
    setClickCount((prev) => prev + 1);

    if (clickTimeoutRef.current) clearTimeout(clickTimeoutRef.current);

    clickTimeoutRef.current = setTimeout(() => {
      if (clickCount >= 2) {
        onTitleClick && onTitleClick();
      }
      setClickCount(0);
    }, 500);
  };

  return (
    <RelativeContainer
      style={{
        height: "auto",
        width: "100%",
        flexFlow: "row",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "20px 40px",
        boxSizing: "border-box",
        zIndex: 1002,
      }}
      className="header-container"
    >
      <div
        style={{
          fontSize: "45px",
          backdropFilter: "blur(2px)",

          ...titleSlideIn,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          gap: "20px",
        }}
        id="header-title"
        onClick={handleTitleClick}
      >
        <span>moodboard builder</span>
        <Info
          size={15}
          style={{ cursor: "pointer", position: "relative", top: "2px" }}
          onClick={(e) => {
            e.stopPropagation();
            setShowInfoModal(true);
          }}
        />
      </div>

      <div
        style={{
          display: "flex",
          gap: "30px",
          alignItems: "center",
          backdropFilter: "blur(2px)",
        }}
        className="header-actions"
      >
        <div
          onClick={onCreateClick}
          style={{
            fontSize: "30px",
            cursor: "pointer",
            ...createButtonSlideIn,
          }}
          className="create-button"
        >
          create a moodboard
        </div>
        <UserSection user={user} logout={logout} slideInStyle={userSlideIn} />
      </div>

      {/* Info Modal */}
      {showInfoModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 1050,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "rgba(255, 255, 255, 0.2)",
            backdropFilter: "blur(8px)",
          }}
          onClick={() => setShowInfoModal(false)}
        >
          <div
            style={{
              position: "relative",
              width: "100%",
              maxWidth: "448px",
              borderRadius: "24px",
              border: "1px solid rgba(255, 255, 255, 0.05)",
              backgroundColor: "#ffffff",
              backdropFilter: "blur(8px)",
              padding: "40px",
              boxShadow: "0 25px 50px rgba(0, 0, 0, 0.8)",
              margin: "0 20px",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button
              onClick={() => setShowInfoModal(false)}
              style={{
                position: "absolute",
                top: "20px",
                right: "20px",
                borderRadius: "50%",
                padding: "8px",
                color: "#000",
                backgroundColor: "transparent",
                border: "none",
                cursor: "pointer",
                transition: "background-color 0.3s",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor =
                  "rgba(255, 255, 255, 0.2)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "transparent";
              }}
              aria-label="Close"
            >
              <X size={20} />
            </button>

            {/* Header */}
            <div style={{ marginBottom: "32px", paddingTop: "4px" }}>
              <h2
                style={{
                  fontSize: "32px",
                  fontWeight: 800,
                  letterSpacing: "normal",
                  color: "#000",
                  marginBottom: "20px",
                  marginTop: 0,
                }}
              >
                welcome to moodboard builder!
              </h2>
              <p
                style={{
                  marginBottom: "16px",
                  lineHeight: "1.6",
                  fontSize: 18,
                }}
              >
                create an account and your first moodboard.
              </p>
              <p
                style={{
                  marginBottom: "16px",
                  lineHeight: "1.6",
                  fontSize: 18,
                }}
              >
                click on the canvas to add an image or gif. you can move and
                resize them.
              </p>
              <p style={{ marginBottom: 0, lineHeight: "1.6", fontSize: 18 }}>
                if your moodboard is public, you can send your URL to anyone.
              </p>
              <p style={{ marginBottom: 0, lineHeight: "1.6", fontSize: 18 }}>
                image uploads are currently limited to 1mb and mobile editing
                features are in progress.
              </p>
              <p style={{ marginBottom: 0, lineHeight: "1.6", fontSize: 18 }}>
                ithe concept of this app was initially created by yung jake and
                the implementation done by tofu
              </p>
            </div>
          </div>
        </div>
      )}
    </RelativeContainer>
  );
}

function ItemContainer({
  children,
  active,
  width,
  index,
  onClick,
  style = {},
}) {
  const mounted = useMounted();
  const slideIn = useSlideIn({
    show: true,
    delay: 500 + (index + 1) * 50,
    duration: 200,
  });
  return (
    <RelativeContainer
      onClick={onClick}
      className="moodboard-tile"
      style={{
        height: mounted ? 180 : 0,
        width: 189.5,
        margin: "10px",
        overflow: "visible",
        background: "white",
        boxShadow: `0 0 4px 1px rgba(0,0,0,${mounted ? 0.15 : 0})`,
        color: "#333",
        borderRadius: "12px",
        cursor: onClick ? "pointer" : "default",
        fontSize: "14px",
        padding: "20px",
        boxSizing: "border-box",
        backgroundColor: "#f3f3f3",
        ...slideIn,
        ...style,
      }}
    >
      {children}
    </RelativeContainer>
  );
}

function MoodboardLinks({ active, path, onDelete, changes }) {
  const [deleteConfirmActive, setDeleteConfirmActive] = useState(false);

  const [copied, setCopied] = useState(false);
  const copiedTimerRef = useRef();

  useEffect(() => {
    if (!active) {
      setDeleteConfirmActive(false);
      setCopied(false);
    }
  }, [active]);

  useEffect(() => {
    if (copiedTimerRef.current) clearTimeout(copiedTimerRef.current);
    if (copied) {
      copiedTimerRef.current = setTimeout(() => {
        setCopied(false);
      }, 1000);
    }
    return () => {
      if (copiedTimerRef.current) clearTimeout(copiedTimerRef.current);
    };
  }, [copied]);

  const shareUrl = `${window.location.origin}/${path}`;

  const onShare = useCallback(async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: "Check out this moodboard!",
          text: "I wanted to share this moodboard with you",
          url: shareUrl,
        });
      } catch (err) {
        if (err.name !== "AbortError") {
          console.log("Share failed:", err);
        }
      }
    } else {
      // Fallback: copy to clipboard
      navigator.clipboard
        .writeText(shareUrl)
        .then(() => {
          setCopied(true);
        })
        .catch((err) => {
          console.log(err);
        });
    }
  }, [shareUrl]);

  const handleDelete = useCallback(() => {
    if (deleteConfirmActive) {
      onDelete(() => {
        setDeleteConfirmActive(false);
      });
    } else {
      setDeleteConfirmActive(true);
    }
  }, [deleteConfirmActive, onDelete]);

  const copiedSlideIn = useSlideIn({
    show: active && copied,
    y: -10,
  });

  return (
    <RelativeContainer
      style={{
        height: 48,
        width: "100%",
        flexFlow: "row",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "0 4px",
        boxSizing: "border-box",
        marginTop: 8,
      }}
    >
      {/* Delete icon */}
      {onDelete && (
        <div
          onClick={handleDelete}
          style={{
            cursor: "pointer",
            padding: 8,
            opacity: active ? 1 : 0,
            transition: "opacity 250ms",
          }}
          title={
            deleteConfirmActive
              ? "Click again to confirm delete"
              : "Delete moodboard"
          }
        >
          <Trash2
            size={24}
            color={deleteConfirmActive ? "#ef4444" : "#000"}
            strokeWidth={2}
          />
        </div>
      )}

      {/* Spacer */}
      <div style={{ flex: 1 }} />

      {/* Share/Upload icon */}
      <div
        onClick={onShare}
        style={{
          cursor: "pointer",
          padding: 8,
          opacity: active ? 1 : 0,
          transition: "opacity 250ms",
        }}
        title="Share moodboard"
      >
        <Upload size={24} color="#000" strokeWidth={2.3} />
      </div>

      {/* Copied notification */}
      <AbsoluteContainer
        style={{
          height: 36,
          width: 96,
          fontSize: "13px",
          fontWeight: 600,
          background: "#ffffff",
          cursor: "pointer",
          borderRadius: 999,
          border: "1px solid rgba(0,0,0,0.08)",
          boxShadow: "0 10px 22px rgba(0,0,0,0.10)",
          ...copiedSlideIn,
        }}
      >
        Copied!
      </AbsoluteContainer>
    </RelativeContainer>
  );
}

function AdminMoodboardPanel({
  onSave,
  name,
  path,
  isPublic,
  id,
  active,
  onClose,
  onDelete,
}) {
  const [editedPath, setEditedPath] = useState(path);
  const [editedName, setEditedName] = useState(name);
  const [editedPublic, setEditedPublic] = useState(isPublic);

  const handleChangePath = useCallback((value) => {
    setEditedPath(encodeInputPath(value));
  }, []);

  const changes = useMemo(
    () =>
      editedPath !== encodeInputPath(path) ||
      editedName !== name ||
      editedPublic !== isPublic,
    [name, path, isPublic, editedPath, editedPublic, editedName]
  );

  const handleSave = useCallback(
    (callback) => {
      let realPath = editedPath;
      if (realPath) {
        if (realPath.indexOf("/ ") === 0) realPath = realPath.substring(2);
      }
      onSave(
        {
          name: editedName,
          path: realPath,
          public: editedPublic || false,
        },
        ({ success, error }) => {
          if (success) {
            setEditedPath(encodeInputPath(path));
            setEditedName(name);
            setEditedPublic(isPublic);
            onClose();
          }
          callback({ success, error });
        }
      );
    },
    [
      onSave,
      path,
      name,
      onClose,
      isPublic,
      editedName,
      editedPath,
      editedPublic,
    ]
  );

  useEffect(() => {
    if (!active) {
      setEditedPath(encodeInputPath(path));
      setEditedName(name);
      setEditedPublic(isPublic);
    }
  }, [active, path, isPublic, name]);

  return (
    <RelativeContainer
      style={{
        width: "100%",
        height: "100%",
        justifyContent: "flex-start",
        alignItems: "stretch",
        gap: 20,
        padding: "25px 15px",
        boxSizing: "border-box",
        opacity: active ? 1 : 0,
        transition: "opacity 250ms",
        transitionDelay: active ? "120ms" : "0ms",
        pointerEvents: active ? "auto" : "none",
      }}
    >
      {/* Save icon in top right */}
      <AbsoluteContainer
        style={{
          top: 0,
          right: 0,
          width: "auto",
          height: "auto",
        }}
      >
        <SaveIcon
          size={38}
          changes={changes}
          onSave={active ? handleSave : null}
          onClose={onClose}
          style={{ color: "#000" }}
        />
      </AbsoluteContainer>

      {/* Visibility section */}
      <div style={{ width: "100%" }}>
        <div
          style={{
            fontSize: 22,
            fontWeight: 400,
            // letterSpacing: "0.02em",
            textTransform: "lowercase",
            color: "#000",
            marginBottom: 14,
          }}
        >
          visibility
        </div>
        <div
          onClick={() => setEditedPublic((pub) => !pub)}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            cursor: "pointer",
            marginBottom: 15,
          }}
        >
          {editedPublic ? (
            <LockOpen size={20} color="#000" />
          ) : (
            <Lock size={20} color="#000" />
          )}
          <span
            style={{
              fontSize: 18,
              color: "#666",
              fontWeight: 400,
            }}
          >
            {editedPublic ? "public" : "unlisted"}
          </span>
        </div>
      </div>

      {/* Moodboard name section */}
      <div style={{ width: "100%" }}>
        <div
          style={{
            fontSize: 20,
            fontWeight: 400,
            // letterSpacing: "0.02em",
            textTransform: "lowercase",
            color: "#000",
            marginBottom: 14,
            paddingBottom: 10,
          }}
        >
          moodboard name
        </div>
        <TextInput
          readOnly={!active}
          value={editedName}
          placeholder=""
          onChange={setEditedName}
          containerStyle={{ width: "100%", height: 52 }}
          style={{
            height: 52,
            background: "#fff",
            border: "1px solid #e0e0e0",
            borderRadius: 16,
            padding: "14px 16px",
            fontSize: 16,
            color: "#111827",
            marginBottom: 30,
          }}
        />
      </div>

      {/* URL path section */}
      <div style={{ width: "100%" }}>
        <div
          style={{
            fontSize: 20,
            fontWeight: 400,
            letterSpacing: "0.02em",
            textTransform: "lowercase",
            color: "#000",
            marginBottom: 12,
          }}
        >
          url path
        </div>
        <TextInput
          readOnly={!active}
          value={editedPath}
          placeholder="/my-moodboard"
          onChange={handleChangePath}
          containerStyle={{ width: "100%", height: 52 }}
          style={{
            height: 52,
            background: "#fff",
            border: "1px solid #e0e0e0",
            borderRadius: 16,
            padding: "14px 16px",
            fontSize: 16,
            color: "#111827",
          }}
        />
      </div>

      {path ? (
        <MoodboardLinks
          active={path}
          path={path}
          onDelete={onDelete}
          changes={changes}
        />
      ) : null}
    </RelativeContainer>
  );
}

function AdminMoodboardThumbnail({
  name,
  path,
  index,
  selected,
  select,
  deselect,
  id,
  public: isPublic,
  adminFunctions,
  user,
  createdBy,
}) {
  const history = useHistory();
  const [showEditPanel, setShowEditPanel] = useState(false);

  const handleSave = useCallback(
    (data, callback) => {
      if (adminFunctions) {
        adminFunctions.saveMoodboardInfo(id, data, callback);
      }
    },
    [adminFunctions, id]
  );

  const handleDelete = useCallback(
    (callback) => {
      if (adminFunctions && id) {
        adminFunctions.deleteMoodboard(id, callback);
      }
    },
    [adminFunctions, id]
  );

  const handleClick = useCallback(() => {
    history.push(path);
  }, [path, history]);

  // Check if current user is the creator of this moodboard
  const isCreator = user && createdBy && user.uid === createdBy;
  const canEdit = isCreator;
  const canToggleLock = isCreator;

  const handleEdit = useCallback(
    (e) => {
      e.stopPropagation();
      if (canEdit) {
        setShowEditPanel(true);
      }
    },
    [canEdit]
  );

  const handleTogglePublic = useCallback(
    (e) => {
      e.stopPropagation();
      // Only allow toggle if user is the creator
      if (adminFunctions && canToggleLock) {
        adminFunctions.saveMoodboardInfo(
          id,
          {
            name,
            path,
            public: !isPublic,
          },
          ({ success, error }) => {
            if (error) {
              console.error("Failed to toggle public state:", error);
            }
          }
        );
      }
    },
    [adminFunctions, id, name, path, isPublic, canToggleLock]
  );

  const tileStyle = isPublic ? {} : { backgroundColor: "#ffc9c9" };

  return (
    <>
      <ItemContainer
        index={index}
        onClick={handleClick}
        style={{
          position: "relative",
          justifyContent: "center",
          alignItems: "center",
          textAlign: "left",
          wordWrap: "break-word",
          ...tileStyle,
        }}
      >
        {canEdit ? (
          <AbsoluteContainer
            onClick={handleEdit}
            style={{
              top: 10,
              left: 10,
              height: 16,
              width: 16,
              // background: "rgba(255,255,255,0.9)",
              // borderRadius: "50%",
              cursor: "pointer",
              fontSize: "14px",
              // boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
            }}
          >
            {/* <span role="img" aria-label="share icon">
              ⚙️
            </span> */}
            <Share />
          </AbsoluteContainer>
        ) : null}
        <AbsoluteContainer
          onClick={canToggleLock ? handleTogglePublic : undefined}
          style={{
            top: 10,
            right: 10,
            height: 16,
            width: 16,
            cursor: canToggleLock ? "pointer" : "default",
            opacity: canToggleLock ? 1 : 0.5,
          }}
        >
          {isPublic ? <LockOpen /> : <Lock />}
        </AbsoluteContainer>
        <div
          style={{
            fontSize: "16px",
            lineHeight: "1.4",
            overflow: "hidden",
            textOverflow: "ellipsis",
            display: "-webkit-box",
            WebkitLineClamp: 6,
            WebkitBoxOrient: "vertical",
            textAlign: "center",
            zIndex: 3,
            // fontWeight: "600",
            // marginTop: "10px",
          }}
        >
          {name}
        </div>
      </ItemContainer>
      {showEditPanel && canEdit ? (
        <AbsoluteContainer
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0,0,0,0.4)",
            backdropFilter: "blur(8px)",
            zIndex: 1000,
          }}
          onClick={() => setShowEditPanel(false)}
        >
          <RelativeContainer
            onClick={(e) => e.stopPropagation()}
            className="modal-content"
            style={{
              width: 340,
              height: "auto",
              background: "linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)",
              borderRadius: 18,
              padding: "24px",
              boxShadow:
                "0 20px 50px rgba(0,0,0,0.15), 0 0 0 1px rgba(0,0,0,0.05)",
            }}
          >
            <AdminMoodboardPanel
              active={true}
              onSave={handleSave}
              onDelete={handleDelete}
              onClose={() => setShowEditPanel(false)}
              path={path}
              name={name}
              isPublic={isPublic}
            />
          </RelativeContainer>
        </AbsoluteContainer>
      ) : null}
    </>
  );
}

function AddMoodboardButton({ onAdd, index }) {
  const [showPanel, setShowPanel] = useState(false);

  const onClick = useCallback(() => {
    setShowPanel(true);
  }, []);

  return (
    <>
      <ItemContainer
        index={index}
        onClick={onClick}
        style={{
          fontSize: "60px",
          fontWeight: "200",
          justifyContent: "center",
          alignItems: "center",
          // boxShadow: "0px 4px 10px #999",
          border: "1px solid black",
        }}
      >
        +
      </ItemContainer>
      {showPanel ? (
        <AbsoluteContainer
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0,0,0,0.4)",
            backdropFilter: "blur(8px)",
            zIndex: 1000,
          }}
          onClick={() => setShowPanel(false)}
        >
          <RelativeContainer
            onClick={(e) => e.stopPropagation()}
            className="modal-content"
            style={{
              width: 340,
              height: "auto",
              background: "linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)",
              borderRadius: 18,
              padding: "24px",
              boxShadow:
                "0 20px 50px rgba(0,0,0,0.15), 0 0 0 1px rgba(0,0,0,0.05)",
            }}
          >
            <AdminMoodboardPanel
              onClose={() => setShowPanel(false)}
              onSave={onAdd}
              active={true}
            />
          </RelativeContainer>
        </AbsoluteContainer>
      ) : null}
    </>
  );
}

function MoodboardList({ moodboards, adminFunctions, user }) {
  const [selectedId, setSelectedId] = useState(null);

  useEffect(() => {
    if (!adminFunctions) {
      setSelectedId(null);
    }
  }, [adminFunctions]);

  const handleAdd = useCallback(
    (moodboardData, callback) => {
      if (adminFunctions) {
        adminFunctions.addMoodboard(moodboardData, ({ id, error }) => {
          if (id) {
            setSelectedId(id);
            callback({ success: true });
          } else {
            callback({ error });
          }
        });
      }
    },
    [adminFunctions]
  );

  return (
    <RelativeContainer
      className="moodboard-grid"
      style={{
        height: "auto",
        width: "100%",
        flexFlow: "row",
        flexWrap: "wrap",
        justifyContent: "flex-start",
        alignItems: "flex-start",
        padding: "20px 30px",
        boxSizing: "border-box",
      }}
    >
      {/* Only show AddMoodboardButton if user is signed in */}
      {moodboards && adminFunctions && adminFunctions.addMoodboard ? (
        <AddMoodboardButton index={0} onAdd={handleAdd} />
      ) : null}
      {moodboards
        ? Object.keys(moodboards)
            .filter((moodboardId) => {
              const moodboard = moodboards[moodboardId];
              // Show if public, or if current user is the creator
              if (moodboard.public) return true;
              if (user && moodboard.createdBy === user.uid) return true;
              return false;
            })
            .map((moodboardId, index) => (
              <AdminMoodboardThumbnail
                adminFunctions={adminFunctions}
                selected={selectedId === moodboardId}
                select={() => setSelectedId(moodboardId)}
                deselect={() => setSelectedId(null)}
                key={moodboardId}
                id={moodboardId}
                index={index + 1}
                user={user}
                {...moodboards[moodboardId]}
              />
            ))
        : null}
    </RelativeContainer>
  );
}

const HomePage = ({
  height,
  history,
  width,
  moodboards: moodboardsData,
  user: userData,
}) => {
  const { loggingIn, user, logout } = userData;
  const { moodboards, adminFunctions } = moodboardsData;
  const [showCreatePanel, setShowCreatePanel] = useState(false);

  // Debug state - show on screen for mobile debugging
  const [showDebug, setShowDebug] = useState(false);

  // Only pass addMoodboard if user is signed in
  const filteredAdminFunctions = user
    ? adminFunctions
    : { ...adminFunctions, addMoodboard: null };

  const handleCreateClick = useCallback(() => {
    if (user && adminFunctions && adminFunctions.addMoodboard) {
      setShowCreatePanel(true);
    }
  }, [user, adminFunctions]);

  const handleAdd = useCallback(
    (moodboardData, callback) => {
      if (adminFunctions) {
        adminFunctions.addMoodboard(moodboardData, ({ id, error }) => {
          if (id) {
            setShowCreatePanel(false);
            callback({ success: true });
          } else {
            callback({ error });
          }
        });
      }
    },
    [adminFunctions]
  );

  return (
    <RelativeContainer
      style={{
        height,
        width,
        justifyContent: "flex-start",
        overflow: "auto",
      }}
    >
      {/* Debug overlay - triple tap title to toggle */}
      {showDebug && (
        <div
          style={{
            position: "fixed",
            top: 60,
            left: 10,
            right: 10,
            background: "rgba(0,0,0,0.9)",
            color: "#0f0",
            padding: "10px",
            fontSize: "12px",
            zIndex: 9999,
            fontFamily: "monospace",
            maxHeight: "200px",
            overflow: "auto",
            borderRadius: "4px",
          }}
        >
          <div>DEBUG INFO:</div>
          <div>loggingIn: {String(loggingIn)}</div>
          <div>user: {user ? "SET" : "NULL"}</div>
          {user && (
            <>
              <div>email: {user.email}</div>
              <div>displayName: {user.displayName || "null"}</div>
              <div>uid: {user.uid?.substring(0, 8)}...</div>
            </>
          )}
          <button
            onClick={() => setShowDebug(false)}
            style={{
              marginTop: "8px",
              background: "#fff",
              color: "#000",
              border: "none",
              padding: "4px 8px",
              borderRadius: "3px",
            }}
          >
            Close
          </button>
        </div>
      )}
      <Header
        user={user}
        onCreateClick={handleCreateClick}
        logout={logout}
        onTitleClick={() => setShowDebug(true)}
      />
      <MoodboardList
        adminFunctions={filteredAdminFunctions}
        moodboards={loggingIn ? null : moodboards}
        user={user}
      />
      {showCreatePanel ? (
        <AbsoluteContainer
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0,0,0,0.4)",
            backdropFilter: "blur(8px)",
            zIndex: 1000,
          }}
          onClick={() => setShowCreatePanel(false)}
        >
          <RelativeContainer
            onClick={(e) => e.stopPropagation()}
            className="modal-content"
            style={{
              width: 340,
              height: "auto",
              background: "linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)",
              borderRadius: 18,
              padding: "24px",
              boxShadow:
                "0 20px 50px rgba(0,0,0,0.15), 0 0 0 1px rgba(0,0,0,0.05)",
            }}
          >
            <AdminMoodboardPanel
              onClose={() => setShowCreatePanel(false)}
              onSave={handleAdd}
              active={true}
            />
          </RelativeContainer>
        </AbsoluteContainer>
      ) : null}
    </RelativeContainer>
  );
};

export default HomePage;
